import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { formatApiError } from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const fillDemo = (role) => {
    if (role === "admin") { setEmail("admin@sanghvi.com"); setPassword("admin@123"); }
    if (role === "cluster_head") { setEmail("clusterhead@sanghvi.com"); setPassword("cluster@123"); }
    if (role === "engineer") { setEmail("engineer@sanghvi.com"); setPassword("engineer@123"); }
    if (role === "warehouse_manager") { setEmail("warehouse@sanghvi.com"); setPassword("warehouse@123"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await login(email, password);
      const to = loc.state?.from?.pathname || "/";
      nav(to, { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row" data-testid="login-page">
      {/* Hero */}
      <div className="hidden md:flex md:w-1/2 relative bg-[#18181B] overflow-hidden">
        <div className="absolute inset-0 dotgrid opacity-10" />
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1760013767150-da8e4ded6286?crop=entropy&cs=srgb&fm=jpg&q=85')" }}
        />
        <div className="relative z-10 m-auto p-12 max-w-md text-white">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#27272A] border border-[#3F3F46] text-[10px] uppercase tracking-widest font-semibold mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F97316]" /> Telecom · ISP · Material Ops
          </div>
          <h1 className="font-display font-extrabold text-5xl leading-[1.05] tracking-tight">
            Inventory built for the field.
          </h1>
          <p className="text-zinc-400 mt-6 text-base leading-relaxed">
            One portal for requests, approvals, issue, recovery, dispatch and circuit-ID material mapping —
            engineered for Sanghvi Infotech&apos;s engineers and warehouse teams.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-4">
            {[["Engineers", "Field-first"], ["Warehouses", "Real-time"], ["E-way Bills", "Tracked"]].map(([k,v]) => (
              <div key={k}>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{k}</div>
                <div className="font-display font-bold text-white mt-1">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12 bg-white">
        <div className="w-full max-w-sm">
          <div className="md:hidden mb-8 flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-[#F97316] flex items-center justify-center font-display font-bold text-white">S</div>
            <div>
              <div className="font-display font-bold">Sanghvi Infotech</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Inventory Portal</div>
            </div>
          </div>
          <div className="text-[11px] uppercase tracking-widest font-bold text-[#F97316]">Sign in</div>
          <h2 className="font-display text-3xl font-bold mt-2">Welcome back.</h2>
          <p className="text-sm text-zinc-500 mt-2">Use your Sanghvi credentials to access the portal.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-700 mb-1.5">Email</label>
              <input
                data-testid="login-email-input"
                type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                className="w-full border border-zinc-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316] focus:border-transparent"
                placeholder="you@sanghvi.com"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-700 mb-1.5">Password</label>
              <input
                data-testid="login-password-input"
                type="password" required value={password} onChange={(e)=>setPassword(e.target.value)}
                className="w-full border border-zinc-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316] focus:border-transparent"
                placeholder="••••••••"
              />
            </div>
            {err && (
              <div data-testid="login-error" className="text-xs font-medium text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>
            )}
            <button
              data-testid="login-submit-button"
              type="submit" disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold text-sm py-2.5 rounded-md transition-colors disabled:opacity-60"
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              Sign in
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
